import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { ISinglePro } from './state';
import { SectionCard, ScopedChip } from './SectionCard';

const bodyText = { color: 'var(--text)', fontSize: 16, lineHeight: 1.5 };

interface ScopedProps {
  expert?: ISinglePro;
  projectId?: string | null;
  scoped?: boolean;
}

export const KeyPointsCard: FC<ScopedProps> = ({ expert, projectId, scoped }) => {
  const points = expert?.keyPoints || [];
  if (points.length === 0) return null;
  return (
    <SectionCard
      span2
      label={scoped ? 'Project key points' : 'Key points'}
      right={scoped && projectId ? <ScopedChip projectId={projectId} /> : undefined}
    >
      <Box className="flex flex-col gap-3">
        {points.map((point, i) => (
          <Box key={`${point.keyPoint}-${i}`} className="flex items-start gap-3">
            <span className="w-[5px] h-[5px] rounded-full bg-accent mt-2 shrink-0" />
            <Typography sx={bodyText}>{point.keyPoint}</Typography>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
};

export const ExpertiseCard: FC<{ expert?: ISinglePro; span2?: boolean }> = ({
  expert,
  span2,
}) => {
  const items = expert?.expertise || [];
  if (items.length === 0) return null;
  return (
    <SectionCard label="Expertise" span2={span2}>
      <Box className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <span
            key={`${item.expertise}-${i}`}
            className="text-[13px] leading-none text-text-2 bg-surface-2 rounded-full px-3 py-2"
          >
            {item.expertise}
          </span>
        ))}
      </Box>
    </SectionCard>
  );
};

interface KnownForProps {
  expert?: ISinglePro;
  canEdit?: boolean;
  span2?: boolean;
}

export const KnownForCard: FC<KnownForProps> = ({ expert, canEdit, span2 }) => {
  const knownFor = expert?.best_known_for?.trim();
  const extras = [
    { label: 'Awards', value: expert?.bio_awards?.trim() },
    { label: 'Notable projects', value: expert?.bio_notable_projects?.trim() },
    { label: 'Favorite achievements', value: expert?.bio_favorite_achievements?.trim() },
  ].filter((e): e is { label: string; value: string } => !!e.value);
  const empty = !knownFor && extras.length === 0;
  if (empty && !canEdit) return null;

  return (
    <SectionCard label="Known for" span2={span2}>
      {!!knownFor && <Typography sx={bodyText}>{knownFor}</Typography>}
      {extras.length > 0 && (
        <Box className="flex flex-col gap-1.5">
          {extras.map((e) => (
            <Typography key={e.label} sx={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)' }}>
              <span style={{ color: 'var(--text)' }}>{e.label}:</span> {e.value}
            </Typography>
          ))}
        </Box>
      )}
      {empty && (
        <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: 'var(--muted)' }}>
          Nothing added yet — add “known for”, awards and notable projects in the profile editor.
        </Typography>
      )}
      {!empty && extras.length === 0 && (
        <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: 'var(--muted)' }}>
          No awards or notable projects added yet{canEdit ? ' — add them in the profile editor.' : '.'}
        </Typography>
      )}
    </SectionCard>
  );
};

export const BiographyCard: FC<ScopedProps> = ({ expert, projectId, scoped }) => {
  const text = expert?.description?.trim();
  if (!text) return null;
  return (
    <SectionCard
      span2
      label={scoped ? 'Project biography' : 'Biography'}
      right={scoped && projectId ? <ScopedChip projectId={projectId} /> : undefined}
    >
      <Typography sx={{ color: 'var(--text-2)', fontSize: 15, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
        {text}
      </Typography>
    </SectionCard>
  );
};
